import { useState, useEffect, Fragment } from 'react'
import { Form as FinalForm, Field } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import { styled, Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { Grid, Typography, Dialog, Button, Divider, Link } from '@mui/material'
import CheckboxInput from '../input/CheckboxFormInput'
import DialogControl from '../../control/Dialog'
import Textbox from '../../control/Textbox'
import CoreHeader from '../map/CoreHeader'
import CoreFooter from '../map/CoreFooter'
import { decryptAES } from '../../util/cipher'
import useLogin, { Form, validate } from '../../hooks/verify/useLogin'
import { ReactComponent as UsernameIcon } from '../../icon/login_username.svg'
import { ReactComponent as PasswordIcon } from '../../icon/login_password.svg'
import { ReactComponent as ShowIcon } from '../../icon/login_password_visible.svg'
import { ReactComponent as HideIcon } from '../../icon/login_password_invisible.svg'
import { useHistory } from 'react-router-dom'

const BgImage = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/bg_blue_dark.png`
const BgLoginImage = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/bg_login_no_logo.png`
const LogoImage = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/logo_main.png`

const Login = () => {
    const {
        onSubmitForm,
        onClickOk,
        openDialogOk,
        alertMessageOk,
        openDialogError,
        alertMessageError,
        setOpenDialogOk,
        setAlertMessageOk,
    } = useLogin()
    const [openDialog, setOpenDialog] = useState(true)
    const [showISYL, setShowISYL] = useState(false)
    const { t } = useTranslation()
    const classes = useStyles()
    const history = useHistory()

    const validates = (values: Form) => {
        const validation = validate(values, t) || {}
        return validation
    }
    const [initialValues, setInitialValues] = useState<Form>({
        username: '',
        isyl: '',
    })

    useEffect(() => {
        if (globalThis.localStorage.getItem(`${process.env.REACT_APP_REMEMBER_USER}`)) {
            setInitialValues({
                username: `${decryptAES(
                    globalThis.localStorage.getItem(`${process.env.REACT_APP_REMEMBER_USER}`) || '',
                )}`,
                isyl: `${decryptAES(globalThis.localStorage.getItem(`${process.env.REACT_APP_REMEMBER_ISYL}`) || '')}`,
                rememberPassword: true,
            })
        }

        let localRefId = globalThis.sessionStorage.getItem(`${process.env.REACT_APP_TOKEN_PROFILE}`)
        if (!localRefId) {
            localRefId = globalThis.localStorage.getItem(`${process.env.REACT_APP_TOKEN_PROFILE}`)
        }

        if (localRefId) {
            history.push('/')
        }

        const search = globalThis.location.search
        const params = new URLSearchParams(search)
        const result = params.get('result')
        if (result && result === 'error') {
            setOpenDialogOk(true)
            setAlertMessageOk('ไม่สามารถเข้าสู่ระบบได้')
        }
    }, [])

    return (
        <Fragment>
            <Dialog
                fullScreen
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false)
                }}
                classes={{ paper: classes.drawerPaper }}
            >
                <CoreHeader />

                <StyledContainer>
                    <StyledBox>
                        <StyledBoxLeft>
                            <img src={LogoImage} className={classes.logo} />
                        </StyledBoxLeft>
                        <StyledBoxRight>
                            <StyledDivForm>
                                <StyledDivTitle>
                                    <StyledTypoTitle>{t('LABEL.LOGIN.TITLE')}</StyledTypoTitle>
                                    <StyledTypoSubTitle>
                                        {'ยินดีต้อนรับเข้าสู่ระบบ DTT Service Area'}
                                    </StyledTypoSubTitle>
                                    <Divider />
                                </StyledDivTitle>
                                <FinalForm onSubmit={onSubmitForm} validate={validates} initialValues={initialValues}>
                                    {({ handleSubmit }) => (
                                        <form onSubmit={handleSubmit}>
                                            <Grid container spacing={2} className={classes.container}>
                                                <Grid item xs={12}>
                                                    <Field
                                                        name="username"
                                                        label={t('LABEL.LOGIN.USERNAME')}
                                                        placeholder={t('LABEL.LOGIN.USERNAME')}
                                                        component={Textbox}
                                                        fullWidth
                                                        required
                                                        startAdornment={<IconUsername />}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Field
                                                        name="isyl"
                                                        type={showISYL ? 'text' : 'password'}
                                                        label={t('LABEL.LOGIN.PASSWORD')}
                                                        placeholder={t('LABEL.LOGIN.PASSWORD')}
                                                        component={Textbox}
                                                        fullWidth
                                                        required
                                                        startAdornment={<IconPassword />}
                                                        endAdornment={
                                                            <Fragment>
                                                                {!showISYL && (
                                                                    <IconShow
                                                                        style={{
                                                                            width: '20px',
                                                                            height: '20px',
                                                                            cursor: 'pointer',
                                                                        }}
                                                                        onClick={() => setShowISYL(true)}
                                                                    />
                                                                )}
                                                                {showISYL && (
                                                                    <IconHide
                                                                        style={{
                                                                            width: '20px',
                                                                            height: '20px',
                                                                            cursor: 'pointer',
                                                                        }}
                                                                        onClick={() => setShowISYL(false)}
                                                                    />
                                                                )}
                                                            </Fragment>
                                                        }
                                                    />
                                                </Grid>
                                                <Grid item xs={12} className={classes.divRememberForgot}>
                                                    <div>
                                                        <Field
                                                            name={'rememberPassword'}
                                                            label={'จดจำการเข้าใช้งาน'}
                                                            component={CheckboxInput}
                                                            type={'checkbox'}
                                                            labelPlacement="end"
                                                            overrideClass={{
                                                                root: classes.checkbox,
                                                                checked: classes.checkboxChecked,
                                                            }}
                                                        />
                                                    </div>
                                                    <StyledForgotPassword>
                                                        <StyledLink
                                                            href={`${process.env.REACT_APP_IMAGE_PATH}/forget-password`}
                                                        >
                                                            {'ลืมรหัสผ่าน'}
                                                        </StyledLink>
                                                    </StyledForgotPassword>
                                                </Grid>
                                                <Grid item xs={12} className={classes.divButtonGroup}>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        type="submit"
                                                        classes={{ root: classes.button }}
                                                    >
                                                        {t('BUTTON.LOG_IN')}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </form>
                                    )}
                                </FinalForm>
                            </StyledDivForm>
                        </StyledBoxRight>
                    </StyledBox>
                </StyledContainer>

                <StyledFooter>
                    <CoreFooter />
                </StyledFooter>
            </Dialog>
            <DialogControl
                type="ok"
                title={t('ALERT.TITLE.NOTIFY')}
                content={alertMessageOk}
                isOpen={openDialogOk}
                onClickOk={onClickOk}
                labelButtonOk={t('BUTTON.OK')}
                disableClickBackdrop
                hideCloseButton
            />
            <DialogControl
                type="ok"
                severity="error"
                title={t('ALERT.TITLE.ERROR')}
                content={alertMessageError}
                isOpen={openDialogError}
                onClickOk={onClickOk}
                labelButtonOk={t('BUTTON.OK')}
                disableClickBackdrop
                hideCloseButton
            />
        </Fragment>
    )
}

export default Login

const useStyles = makeStyles((theme: Theme) => ({
    drawerPaper: {
        backgroundImage: `url(${BgImage})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%',
    },
    container: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    divRememberForgot: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
        },
    },
    checkbox: {
        color: `${theme.palette.primary.main} !important`,
        padding: '5px !important',
        fontSize: '14px !important',
        marginLeft: '5px !important',
    },
    checkboxChecked: {
        color: `${theme.palette.primary.main} !important`,
        padding: '5px !important',
        marginLeft: '5px !important',
    },
    divButtonGroup: {
        display: 'flex',
        justifyContent: 'center',
    },
    divButton: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginLeft: theme.spacing(1),
    },
    button: {
        width: '100%',
        minHeight: '37px',
        fontSize: '16px !important',
        boxShadow: `${theme.palette.getContrastText('shadow-bottom-blue')} !important`,
    },
    logo: {
        height: 'auto',
        position: 'relative',
        width: 300,
        top: 40,
        [theme.breakpoints.down('lg')]: {
            width: 240,
            top: 20,
        },
    },
}))

const StyledFooter = styled('div')(({ theme }) => ({
    position: 'fixed',
    bottom: 0,
    width: '100%',
}))

const StyledContainer = styled('div')(({ theme }) => ({
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    width: 1120,
    marginLeft: 'auto',
    marginRight: 'auto',
    [theme.breakpoints.down('lg')]: {
        width: 860,

        padding: '8px 64px',
    },
    [theme.breakpoints.down('md')]: {
        width: 'auto',
        marginLeft: 0,
        marginRight: 0,
        padding: '6px 32px',
    },
    [theme.breakpoints.down('sm')]: {
        padding: '4px 20px',
    },
}))

const StyledBox = styled('div')(({ theme }) => ({
    width: '100%',
    height: '80%',
    display: 'flex',
    justifyContent: 'space-between',
    borderRadius: '5px',
    boxShadow: theme.palette.getContrastText('shadow-box-blue'),
}))

const StyledBoxLeft = styled('div')(({ theme }) => ({
    width: 480,
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    backgroundImage: `url(${BgLoginImage})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100% 100%',
    borderRadius: '5px 0px 0px 5px',
    [theme.breakpoints.down('lg')]: {
        width: 500,
    },
    [theme.breakpoints.down('md')]: {
        display: 'none',
    },
}))

const StyledBoxRight = styled('div')(({ theme }) => ({
    width: 640,
    height: '100%',
    borderRadius: '5px',
    backgroundColor: theme.palette.getContrastText('bg-login-right'),
    [theme.breakpoints.down('lg')]: {
        width: 540,
    },
    [theme.breakpoints.down('md')]: {
        width: 'auto',
        flexGrow: 1,
    },
    [theme.breakpoints.down('sm')]: {
        overflow: 'auto',
    },
}))

const StyledDivForm = styled('div')(({ theme }) => ({
    padding: '20px 50px',
    [theme.breakpoints.down('sm')]: {
        padding: '10px 30px',
    },
}))

const StyledDivTitle = styled('div')(({ theme }) => ({
    padding: '20px 0px',
}))

const StyledTypoTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.primary.dark,
    fontWeight: 'bold',
    fontSize: '20px',
}))

const StyledTypoSubTitle = styled(Typography)(({ theme }) => ({
    fontSize: '14px',
    margin: '10px 0px',
}))

const StyledLink = styled(Link)(({ theme }) => ({
    fontSize: '14px',
    color: `${theme.palette.primary.dark} !important`,
    textDecorationColor: `${theme.palette.primary.dark} !important`,
}))

const StyledForgotPassword = styled('div')(({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
}))

const IconUsername = styled(UsernameIcon)(({ theme }) => ({
    width: '14px',
}))

const IconPassword = styled(PasswordIcon)(({ theme }) => ({
    width: '14px',
}))

const IconShow = styled(ShowIcon)(({ theme }) => ({
    width: '14px',
}))

const IconHide = styled(HideIcon)(({ theme }) => ({
    width: '14px',
}))
